/**
 * SuccessWare React Query Hooks
 * 
 * Provides React Query hooks for SuccessWare import monitoring.
 * These hooks manage activity stats, recent activity, and sync states
 * with appropriate caching strategies for live monitoring.
 */

import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { successWareService } from '@/lib/services';
import type { SWActivityStats, SWCustomer, SWSyncState } from '@/lib/repositories/SuccessWareRepository';

/**
 * Query Keys
 */
export const successWareKeys = {
  all: ['successware'] as const,
  activityStats: () => [...successWareKeys.all, 'activity-stats'] as const,
  recentActivity: (limit?: number) => [...successWareKeys.all, 'recent-activity', limit] as const,
  syncStates: () => [...successWareKeys.all, 'sync-states'] as const,
};

/**
 * Hook to fetch SuccessWare activity statistics
 * 
 * Returns real-time counts of customers, service accounts, locations,
 * and recent imports (last 5 minutes).
 * 
 * Cache: 30 seconds (dynamic stats data)
 * Refetch: On window focus
 * 
 * @example
 * ```tsx
 * const { data: stats, isLoading } = useSWActivityStats();
 * 
 * if (stats) {
 *   console.log(`Total customers: ${stats.totalCustomers}`);
 *   console.log(`Recent imports: ${stats.recentImports}`);
 * }
 * ```
 */
export function useSWActivityStats(
  options?: UseQueryOptions<SWActivityStats, Error>
) {
  return useQuery<SWActivityStats, Error>({
    queryKey: successWareKeys.activityStats(),
    queryFn: () => successWareService.getActivityStats(),
    staleTime: 30 * 1000, // 30 seconds - dynamic stats
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Hook to fetch recent SuccessWare customer activity
 * 
 * Returns the most recent customer imports, ordered by created_at.
 * 
 * Cache: 30 seconds (dynamic activity feed)
 * Refetch: On window focus
 * 
 * @param limit - Maximum number of records to return (default: 50)
 * 
 * @example
 * ```tsx
 * const { data: activity = [], isLoading } = useSWRecentActivity(50);
 * 
 * return (
 *   <ul>
 *     {activity.map(customer => (
 *       <li key={customer.id}>{customer.company_name}</li>
 *     ))}
 *   </ul>
 * );
 * ```
 */
export function useSWRecentActivity(
  limit: number = 50,
  options?: UseQueryOptions<SWCustomer[], Error>
) {
  return useQuery<SWCustomer[], Error>({
    queryKey: successWareKeys.recentActivity(limit),
    queryFn: () => successWareService.getRecentActivity(limit),
    staleTime: 30 * 1000, // 30 seconds - dynamic activity feed
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Hook to fetch SuccessWare sync states
 * 
 * Returns the current sync status for all companies, including last sync times
 * and status information.
 * 
 * Cache: 1 minute (relatively stable sync state)
 * Refetch: On window focus
 * 
 * @example
 * ```tsx
 * const { data: syncStates = [], isLoading } = useSWSyncStates();
 * 
 * return (
 *   <ul>
 *     {syncStates.map(state => (
 *       <li key={state.company_id}>
 *         {state.last_sync_status} - {new Date(state.last_incremental_sync).toLocaleString()}
 *       </li>
 *     ))}
 *   </ul>
 * );
 * ```
 */
export function useSWSyncStates(
  options?: UseQueryOptions<SWSyncState[], Error>
) {
  return useQuery<SWSyncState[], Error>({
    queryKey: successWareKeys.syncStates(),
    queryFn: () => successWareService.getSyncStates(),
    staleTime: 60 * 1000, // 1 minute - relatively stable
    refetchOnWindowFocus: true,
    ...options,
  });
}

/**
 * Combined hook for SuccessWare monitoring dashboard
 * 
 * Fetches all data needed for the SuccessWare Activity Monitor page:
 * - Activity statistics
 * - Recent customer activity (50 records)
 * - Sync states
 * 
 * This hook combines multiple queries for convenience, allowing components
 * to fetch all monitoring data with a single hook call.
 * 
 * @example
 * ```tsx
 * function SuccessWareMonitor() {
 *   const { stats, activity, syncStates, isLoading } = useSuccessWareMonitor();
 *   
 *   if (isLoading) return <div>Loading...</div>;
 *   
 *   return (
 *     <div>
 *       <h2>Total Customers: {stats?.totalCustomers}</h2>
 *       <ActivityFeed data={activity} />
 *       <SyncStatus states={syncStates} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useSuccessWareMonitor() {
  const statsQuery = useSWActivityStats();
  const activityQuery = useSWRecentActivity(50);
  const syncStatesQuery = useSWSyncStates();

  return {
    // Data
    stats: statsQuery.data,
    activity: activityQuery.data ?? [],
    syncStates: syncStatesQuery.data ?? [],
    
    // Loading states
    isLoading: statsQuery.isLoading || activityQuery.isLoading || syncStatesQuery.isLoading,
    isError: statsQuery.isError || activityQuery.isError || syncStatesQuery.isError,
    
    // Individual query states (for granular control if needed)
    statsQuery,
    activityQuery,
    syncStatesQuery,
    
    // Refetch all data
    refetch: () => {
      statsQuery.refetch();
      activityQuery.refetch();
      syncStatesQuery.refetch();
    },
  };
}
